import React from 'react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';

import FormField from '@/components/forms/form-field';
import AudioUpload from '@/components/forms/audio-upload';
import ManyAutocompleteField from '@/components/forms/many-autocomplete';
import WithSavedFile from '@/components/hoc/with-saved-file';
import { FORMS } from '@/static/common.js';
import { arrangeData } from '@/middlewares/api';
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';

const validationSchema = yup.object({
    artist_name: yup.string().required('Artist name is required').max(255, 'Artist name must be at most 255 characters'),
    song_new_name: yup.string().required('Song name is required').max(255, 'Song name must be at most 255 characters'),
    song_writers: yup.array().nullable(),
    song_publishers: yup.array().nullable(),
    song_url: yup.string().required('Please upload your song').max(255, 'Song URL must be at most 255 characters'),
});

// AudioUpload uses meta.dispatch(stopSubmit(...)) for errors - map to Formik setFieldError.
// Also uses input.onChange(value) with raw value, not event.
const audioUploadAdapter = ({ field, form, ...rest }) => (
    <AudioUpload
        input={{
            ...field,
            onChange: (x) => {
                if (x && typeof x === 'object' && x.target !== undefined) {
                    form.setFieldValue(field.name, x.target.value);
                } else {
                    form.setFieldValue(field.name, x);
                }
            },
            onBlur: (e) => {
                form.setFieldTouched(field.name, true);
                if (field.onBlur) field.onBlur(e);
            },
        }}
        meta={{
            touched: form.touched[field.name],
            error:
                form.touched[field.name] || form.submitCount > 0
                    ? form.errors[field.name]
                    : undefined,
            submitFailed: form.submitCount > 0,
            form: 'upload-sample-step2',
            dispatch: (action) => {
                if (action && action.type === 'redux-form/STOP_SUBMIT' && action.errors) {
                    const key = Object.keys(action.errors)[0];
                    if (key) {
                        // Touch without revalidating, or the schema error would
                        // immediately overwrite the upload failure message.
                        form.setFieldTouched(key, true, false);
                        form.setFieldError(key, action.errors[key]);
                    }
                }
            },
        }}
        {...rest}
    />
);

const FormFieldFormik = formikFieldAdapter(FormField);
const ManyAutocompleteFieldFormik = formikFieldAdapter(ManyAutocompleteField);

const initialValuesShape = {
    song_url: '',
    artist_name: '',
    song_new_name: '',
    song_writers: [],
    song_publishers: [],
};

function UploadSampleStep2({
    nextStep,
    prevStep,
    initialValues = {},
    bgImg,
    getRawFileData,
    setRawFileData,
}) {
    const values = {
        ...initialValuesShape,
        ...initialValues,
    };

    const stepSubmit = (data, formikBag) => {
        const formData = arrangeData(data, null, ['song_writers', 'song_publishers']);
        if (typeof formData.raw_audio !== 'undefined') delete formData.raw_audio;
        const payload = { ...data, raw_audio: getRawFileData() };
        nextStep(formData, payload);
    };

    const goBack = (currentValues) => {
        const rawValues = { ...currentValues, raw_audio: getRawFileData() };
        prevStep(rawValues);
    };

    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SAMPLES</h2>
                    <h3 className="head">Step 2 of 4: YOUR SONG</h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={values}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={(data) => stepSubmit(data)}
                    >
                        {({ values: currentValues, isSubmitting }) => (
                            <Form className="form-layout" noValidate>
                                <div className="form-row">
                                    <div className="form-col_2 full-height">
                                        <Field
                                            className="form-field--red-label"
                                            name="song_url"
                                            fileName="song"
                                            cachedData={initialValues.raw_audio}
                                            uploadUrl="clear/sample/upload-music-file"
                                            component={audioUploadAdapter}
                                            required={true}
                                            label="Upload Your Song (Must be Complete with Vocals)"
                                            type="file"
                                            saveRawFileData={setRawFileData}
                                            note="200 MB maximum file size."
                                            maxFileSize="200"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="artist_name"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Set Artist First and Last Name"
                                            label="Name of Your Artist"
                                        />
                                        <Field
                                            name="song_new_name"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Name of Your New Song"
                                            label="Name of Your New Song"
                                        />
                                    </div>
                                </div>
                                <Field
                                    name="song_writers"
                                    component={ManyAutocompleteFieldFormik}
                                    url="clear/writer/search"
                                    placeholder={FORMS.PLACEHOLDER_TBD}
                                    label="Who are the writers of your song? [Enter TBD if Unknown]"
                                />
                                <Field
                                    name="song_publishers"
                                    component={ManyAutocompleteFieldFormik}
                                    url="clear/publisher/search"
                                    placeholder={FORMS.PLACEHOLDER_TBD}
                                    label="Who are the publishers of your song? [Enter TBD if Unknown]"
                                />
                                <div className="submit-row submit-row_txt">
                                    <button
                                        className="btn btn-second"
                                        onClick={() => goBack(currentValues)}
                                        type="button"
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="btn-red-primary"
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Next
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
}

export default WithSavedFile(UploadSampleStep2);
